import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker } from '../../components/DatePicker';
import { todayKey } from '../popup/TodoSection';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { fetchAuditEntry, fetchAuditPage } from '../../api/audit';
import { NodeChip } from '../../components/NodeChip';
import {
  ACTOR_KIND_LABEL,
  DEFAULT_AUDIT_FILTERS,
  OUTCOME_LABEL,
  PAGE_SIZES,
  actorInitials,
  auditFiltersActive,
  auditQueryOf,
  detailPairs,
  outcomeTone,
  pageCountOf,
  pageWindow,
  rangeLabel,
  statusHint,
  type AuditEntry,
  type AuditFacets,
  type AuditFilters,
  type AuditPageSize,
  type AuditRow,
  type AuditVocabulary,
} from '../../lib/auditLog';

/**
 * THE ACTION LOG (V0.49) — every act by every person and by the AI.
 *
 * A table over `GET /api/audit`, behind `SHOW_LOGS`. Three things about it are worth stating
 * because each was a decision:
 *
 * EVERY FILTER GOES TO THE SERVER, unlike the document history beside it, whose issues filter
 * and sort apply to the rows already loaded. Nothing here spans an unindexed column, so there
 * is no reason to narrow a page after fetching it — and the count can therefore say one honest
 * number for "how many exist" rather than two.
 *
 * THE LABELS COME FROM THE SERVER, riding page 0. The vocabulary of actions and areas is one
 * table in `server/src/audit/vocabulary.ts`, and a second copy here would drift the first time
 * somebody added a verb.
 *
 * A ROW EXPANDS RATHER THAN NAVIGATING. The list withholds `detail` — fifty rows should not
 * ship fifty payloads — so opening one fetches that entry in full. A row that produced a
 * document version links to the drawer the document history already has, rather than drawing a
 * second one.
 */

export function ActionsView({ onLeave }: { onLeave: () => void }) {
  const s = useAppState();

  const [text, setText] = useState(''); // what is typed
  const [filters, setFilters] = useState<AuditFilters>({ ...DEFAULT_AUDIT_FILTERS });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState<AuditPageSize>(50);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState<number | null>(null);
  const [vocabulary, setVocabulary] = useState<AuditVocabulary | null>(null);
  const [facets, setFacets] = useState<AuditFacets | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeq, setRefreshSeq] = useState(0);

  const [openId, setOpenId] = useState<string | null>(null);
  const [entry, setEntry] = useState<AuditEntry | null>(null);
  const [entryError, setEntryError] = useState<string | null>(null);

  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Typing is debounced into the filter; the filter is what the request is keyed on. Same split
  // as the document history's search, and for the same reason: without it eight characters are
  // eight round trips whose answers arrive out of order.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setFilters((f) => (f.text === text ? f : { ...f, text }));
      setPage(0);
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [text]);

  const query = useMemo(() => auditQueryOf(filters, page, perPage), [filters, page, perPage]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchAuditPage(query).then(
      (d) => {
        if (!live) return;
        setTotal(d.total);
        // A PAGE REPLACES, it never appends (V0.49.1). "Load more" grew one list; a pager shows
        // one page, and the count line can therefore say which rows these are.
        setRows(d.events);
        setCapped(d.capped ?? null);
        if (d.vocabulary) setVocabulary(d.vocabulary);
        if (d.facets) setFacets(d.facets);
        setLoading(false);
      },
      (e: Error) => {
        if (!live) return;
        setError(e.message);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [query, refreshSeq]);

  // The open row's full entry, fetched by id. Cleared first, so a slow load never shows one
  // entry's facts under another's heading.
  useEffect(() => {
    if (!openId) return;
    let live = true;
    setEntry(null);
    setEntryError(null);
    fetchAuditEntry(openId).then(
      (e) => live && setEntry(e),
      (e: Error) => live && setEntryError(e.message),
    );
    return () => {
      live = false;
    };
  }, [openId]);

  const narrow = useCallback((patch: Partial<AuditFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  }, []);

  const clearAll = () => {
    setText('');
    setFilters({ ...DEFAULT_AUDIT_FILTERS });
    setPage(0);
  };

  /** The export takes the CURRENT filters and no page — what you see is what you get. */
  const exportHref = `/api/audit/export?${auditQueryOf(filters, 0)}`;

  const pageCount = pageCountOf(total, perPage);

  const actionsForArea = (vocabulary?.actions ?? []).filter(
    (a) => !filters.area || a.area === filters.area,
  );
  const labelOf = (action: string) =>
    vocabulary?.actions.find((a) => a.key === action)?.label ?? action;

  /** The subject cell: the app's own node chip when we have the node, else a typed chip. */
  const subjectOf = (r: AuditRow) => {
    if (!r.targetType && !r.nodeId) return <span className="al-none">—</span>;
    const node = r.nodeId ? s.nodes[r.nodeId] : undefined;
    if (r.targetType === 'node' && node) {
      return (
        <NodeChip
          node={node}
          isLight={s.isLight}
          title="Show this node on the map"
          onClick={() => {
            onLeave();
            void callAction('revealNodeOnMap', r.nodeId!);
          }}
        />
      );
    }
    return (
      <>
        <button
          className="al-chip"
          title="Show everything that happened to this"
          onClick={() => narrow({ targetType: r.targetType ?? '', targetId: r.targetId ?? '', nodeId: '' })}
        >
          {r.targetType ? <span className="ty">{r.targetType}</span> : null}
          <span className="al-chip-name">{r.targetLabel || r.targetId || '—'}</span>
        </button>
        {node ? <span className="al-on-crumb">in {node.label}</span> : null}
      </>
    );
  };

  const active = auditFiltersActive(filters);

  return (
    <div id="dash-log-tab">
      <div className="perm-section-lead">
        Every action taken in this installation — by a person, by the AI, by a background job or
        by an embedded widget’s key. What was done, to what, by whom, and whether it went
        through. Never what anybody merely read.
      </div>

      {/* FACETS: how many rows each value WOULD return, counted with the other filters
          applied and this one removed. That is what makes a chip worth reading before it is
          clicked — a count that only ever showed the current selection would tell nobody
          anything. Clicking toggles, so a second click is how you get back. */}
      {facets ? (
        <div id="audit-facets">
          <FacetRow
            label="Outcome"
            id="audit-facet-outcome"
            counts={facets.outcome}
            current={filters.outcome}
            nameOf={(k) => OUTCOME_LABEL[k as keyof typeof OUTCOME_LABEL] ?? k}
            toneOf={(k) => `tone-${outcomeTone(k as Parameters<typeof outcomeTone>[0])}`}
            onPick={(k) => narrow({ outcome: filters.outcome === k ? '' : k })}
          />
          <FacetRow
            label="Who"
            id="audit-facet-actor"
            counts={facets.actorKind}
            current={filters.actorKind}
            nameOf={(k) => ACTOR_KIND_LABEL[k as keyof typeof ACTOR_KIND_LABEL] ?? k}
            // The avatar colours from the table, so the chip and the rows it filters to agree.
            toneOf={(k) => `who-${k}`}
            onPick={(k) => narrow({ actorKind: filters.actorKind === k ? '' : k })}
          />
          <FacetRow
            label="Area"
            id="audit-facet-area"
            counts={facets.area}
            current={filters.area}
            nameOf={(k) => vocabulary?.areas.find((a) => a.key === k)?.label ?? k}
            onPick={(k) => narrow({ area: filters.area === k ? '' : k, action: '' })}
          />
        </div>
      ) : null}

      <div className="logs-controls">
        <input
          className="fld"
          id="audit-search"
          type="search"
          placeholder="Search what happened, who did it, what it was done to…"
          style={{ flex: 1, minWidth: 230 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <select
          className="fld"
          id="audit-area"
          style={{ width: 'auto' }}
          value={filters.area}
          onChange={(e) => narrow({ area: e.target.value, action: '' })}
        >
          <option value="">All areas</option>
          {(vocabulary?.areas ?? []).map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          className="fld"
          id="audit-action"
          style={{ width: 'auto' }}
          value={filters.action}
          onChange={(e) => narrow({ action: e.target.value })}
        >
          <option value="">Any action</option>
          {actionsForArea.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          className="fld"
          id="audit-actor"
          style={{ width: 'auto' }}
          value={filters.actorKind}
          onChange={(e) => narrow({ actorKind: e.target.value })}
        >
          <option value="">Anyone</option>
          <option value="user">People</option>
          <option value="ai">The AI</option>
          <option value="system">Background jobs</option>
          <option value="api-key">Widget keys</option>
          <option value="anonymous">Not signed in</option>
        </select>
        <select
          className="fld"
          id="audit-outcome"
          style={{ width: 'auto' }}
          value={filters.outcome}
          onChange={(e) => narrow({ outcome: e.target.value })}
        >
          <option value="">Any outcome</option>
          <option value="ok">Went through</option>
          <option value="refused">Refused</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
        {/* The app's one date control (V0.51.1). NO quick picks here: "tomorrow" is a plan and
            this is a bound on a log of things that have already happened. Each end constrains
            the other, so the range cannot be inverted into a query that returns nothing. */}
        <span className="al-dates">
          from
          <DatePicker
            id="audit-from"
            className="fld"
            value={filters.from || null}
            today={todayKey()}
            max={filters.to || null}
            placeholder="Any day"
            onChange={(day) => narrow({ from: day ?? '' })}
          />
          to
          <DatePicker
            id="audit-to"
            className="fld"
            value={filters.to || null}
            today={todayKey()}
            min={filters.from || null}
            placeholder="Any day"
            onChange={(day) => narrow({ to: day ?? '' })}
          />
        </span>
        <button
          className="iconbtn"
          id="audit-refresh"
          title="Refresh"
          onClick={() => {
            setPage(0);
            setRefreshSeq((n) => n + 1);
          }}
        >
          <svg className="ico">
            <use href="#i-refresh" />
          </svg>
        </button>
        <span id="audit-count" className="tgt-meta">
          {rows.length} shown · {total} total
        </span>
        {active ? (
          <button className="btn" id="audit-clear" onClick={clearAll}>
            Clear filters
          </button>
        ) : null}
        {/* A PLAIN LINK, not a fetch-and-blob: the browser streams the file straight to disk,
            which is the difference between exporting a large log and holding it in memory
            twice. It carries the CURRENT filters and no page — what you see is what you get —
            and downloading it is itself an act the log records. */}
        <a className="btn" id="audit-export" href={exportHref} download title="Download these entries as CSV">
          <svg className="ico">
            <use href="#i-export" />
          </svg>
          <span>Export</span>
        </a>
      </div>

      {/* TWO PAGERS, top and bottom, deliberately. A page of 200 rows is taller than the
          window, and a control that only exists under the table makes the reader scroll past
          everything to reach it and then scroll back. They are one component, so they cannot
          disagree. */}
      <Pager
        id="audit-pager-top"
        page={page}
        pageCount={pageCount}
        perPage={perPage}
        shown={rows.length}
        total={total}
        capped={capped}
        loading={loading}
        onPage={setPage}
        onPerPage={(n) => {
          setPerPage(n);
          setPage(0);
        }}
      />

      <div className="card">
        <table className="utable" id="audit-table">
          <colgroup>
            <col style={{ width: '128px' }} />
            <col style={{ width: '168px' }} />
            <col style={{ width: '178px' }} />
            <col style={{ width: '210px' }} />
            <col style={{ width: '112px' }} />
            <col />
            <col style={{ width: '44px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>On</th>
              <th>Outcome</th>
              <th>What happened</th>
              <th />
            </tr>
          </thead>
          <tbody id="audit-tbody">
            {error ? (
              <tr>
                <td colSpan={7} style={{ color: 'var(--ink-faint)' }}>
                  Could not load the log ({error}).
                </td>
              </tr>
            ) : loading && !rows.length ? (
              <tr>
                <td colSpan={7} style={{ color: 'var(--ink-faint)' }}>
                  Loading…
                </td>
              </tr>
            ) : !rows.length ? (
              <tr>
                <td colSpan={7} style={{ color: 'var(--ink-faint)' }}>
                  {active
                    ? 'Nothing matches these filters. The log is append-only, so an empty table means the filters, not a gap.'
                    : 'Nothing has been recorded yet.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const open = openId === r.id;
                const tone = outcomeTone(r.outcome);
                const when = new Date(r.at);
                return [
                  <tr key={r.id} className={`al-row ${tone}${open ? ' open' : ''}`}>
                    <td className="al-when">
                      {when.toLocaleDateString()}
                      <span className="t">{when.toLocaleTimeString().slice(0, 5)}</span>
                    </td>
                    <td>
                      <button
                        className="al-who"
                        title="Show everything this person or agent did"
                        onClick={() =>
                          narrow(
                            r.actorUserId
                              ? { actorUserId: r.actorUserId, actorKind: '' }
                              : { actorKind: r.actorKind, actorUserId: '' },
                          )
                        }
                      >
                        <span className={`al-av ${r.actorKind}`}>{actorInitials(r)}</span>
                        <span className="al-whoname">
                          {r.actorLabel}
                          <span className="al-kind">{ACTOR_KIND_LABEL[r.actorKind]}</span>
                        </span>
                      </button>
                    </td>
                    <td>
                      <button className="al-act" onClick={() => narrow({ action: r.action })}>
                        {labelOf(r.action)}
                        <span className="al-key">{r.action}</span>
                      </button>
                    </td>
                    <td>{subjectOf(r)}</td>
                    <td>
                      <span
                        className={`al-out ${tone}`}
                        tabIndex={0}
                        title={
                          open && entry
                            ? statusHint(entry)
                            : 'Open the row for the status code it came from'
                        }
                      >
                        {OUTCOME_LABEL[r.outcome]}
                      </span>
                    </td>
                    <td className="al-sum">{r.summary}</td>
                    <td>
                      <button
                        className="al-exp"
                        aria-expanded={open}
                        aria-label={open ? 'Hide details' : 'Show details'}
                        onClick={() => setOpenId(open ? null : r.id)}
                      >
                        <svg className="ico">
                          <use href="#i-chev" />
                        </svg>
                      </button>
                    </td>
                  </tr>,
                  open ? (
                    <tr key={`${r.id}:detail`} className="al-detail">
                      <td colSpan={7}>
                        <Drawer entry={entry} error={entryError} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })
            )}
          </tbody>
        </table>
      </div>

      <Pager
        id="audit-pager-bottom"
        page={page}
        pageCount={pageCount}
        perPage={perPage}
        shown={rows.length}
        total={total}
        capped={capped}
        loading={loading}
        onPage={setPage}
        onPerPage={(n) => {
          setPerPage(n);
          setPage(0);
        }}
      />
    </div>
  );
}

/**
 * One row of facet chips: a value, and how many rows it would return.
 *
 * A ZERO IS STILL SHOWN, greyed and unclickable, rather than dropped. A facet list that
 * silently loses its empty values changes shape as you filter, so the chip you were about to
 * click moves — and "no refusals in this window" is itself the answer somebody came for.
 */
function FacetRow({
  label,
  id,
  counts,
  current,
  nameOf,
  toneOf,
  onPick,
}: {
  label: string;
  id: string;
  counts: Record<string, number>;
  current: string;
  nameOf: (key: string) => string;
  /** The dot's colour class — `tone-*` for severity, `who-*` for an actor kind. */
  toneOf?: (key: string) => string;
  onPick: (key: string) => void;
}) {
  const keys = Object.keys(counts);
  if (!keys.length) return null;
  // Busiest first, so the chip somebody wants is usually the one nearest the label.
  keys.sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || a.localeCompare(b));
  return (
    <div className="audit-facet-row" id={id}>
      <span className="audit-facet-label">{label}</span>
      {keys.map((k) => {
        const n = counts[k] ?? 0;
        const on = current === k;
        return (
          <button
            key={k}
            className={`dt-strip-chip audit-facet${on ? ' on' : ''}${n ? '' : ' empty'}`}
            aria-pressed={on}
            disabled={!n && !on}
            onClick={() => onPick(k)}
            title={on ? `Stop filtering by ${nameOf(k)}` : `Show only ${nameOf(k)}`}
          >
            {toneOf ? <span className={`dt-dot ${toneOf(k)}`} /> : null}
            <span>{nameOf(k)}</span>
            <b>{n.toLocaleString()}</b>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The pager: page size, which page, and what is on screen.
 *
 * IT REPLACED "LOAD MORE" (V0.49.1). That control grew ONE list, so after three presses the
 * table held 150 rows and could not say which 150 — and on a log that only grows, "keep
 * appending" is a way of never reaching the older end at all. A pager can be jumped to the
 * last page, which is the thing somebody auditing an incident actually wants.
 *
 * The page numbers come from `pageWindow()` — first, last, and a window around the current —
 * so the control stays the same size whether the log holds 300 rows or 300 000.
 */
function Pager({
  id,
  page,
  pageCount,
  perPage,
  shown,
  total,
  capped,
  loading,
  onPage,
  onPerPage,
}: {
  id: string;
  page: number;
  pageCount: number;
  perPage: AuditPageSize;
  shown: number;
  total: number;
  capped: number | null;
  loading: boolean;
  onPage: (p: number) => void;
  onPerPage: (n: AuditPageSize) => void;
}) {
  return (
    <div className="audit-pager" id={id}>
      <span className="audit-range">{rangeLabel(page, perPage, shown, total)}</span>
      {/* Said plainly rather than implied: these are the newest N of something larger. */}
      {capped ? (
        <span className="audit-capped" title="Narrow the dates or the filters to reach the rest">
          newest {capped.toLocaleString()} shown
        </span>
      ) : null}

      <label className="audit-size">
        <span>Rows</span>
        <select
          className="fld"
          value={String(perPage)}
          onChange={(e) => onPerPage(e.target.value === 'all' ? 'all' : (Number(e.target.value) as AuditPageSize))}
        >
          {PAGE_SIZES.map((n) => (
            <option key={String(n)} value={String(n)}>
              {n === 'all' ? 'All' : n}
            </option>
          ))}
        </select>
      </label>

      {pageCount > 1 ? (
        <div className="audit-pages">
          <button className="pgbtn" disabled={loading || page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page">
            ‹
          </button>
          {pageWindow(page, pageCount).map((p, i) =>
            p === null ? (
              <span key={`gap${i}`} className="audit-gap">…</span>
            ) : (
              <button
                key={p}
                className={`pgbtn${p === page ? ' on' : ''}`}
                aria-current={p === page ? 'page' : undefined}
                disabled={loading}
                onClick={() => onPage(p)}
              >
                {p + 1}
              </button>
            ),
          )}
          <button
            className="pgbtn"
            disabled={loading || page >= pageCount - 1}
            onClick={() => onPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** What one entry recorded, beyond the sentence the table already shows. */
function Drawer({ entry, error }: { entry: AuditEntry | null; error: string | null }) {
  if (error) {
    return (
      <div className="al-dwrap">
        <div className="ld-sec" style={{ color: 'var(--ink-faint)' }}>
          Could not load this entry ({error}).
        </div>
      </div>
    );
  }
  if (!entry) {
    return (
      <div className="al-dwrap">
        <div className="ld-sec" style={{ color: 'var(--ink-faint)' }}>
          Loading…
        </div>
      </div>
    );
  }

  const pairs = detailPairs(entry.detail);
  const revisionId =
    entry.detail && typeof entry.detail.revisionId === 'string' ? entry.detail.revisionId : null;

  return (
    <div className="al-dwrap">
      <div className="ld-sec">
        <h4>Entry</h4>
        <dl className="ld-kv">
          <dt>When</dt>
          <dd>{new Date(entry.at).toLocaleString()}</dd>
          <dt>Who</dt>
          <dd>
            {entry.actorLabel} ({ACTOR_KIND_LABEL[entry.actorKind]})
          </dd>
          <dt>Action</dt>
          <dd className="mono">{entry.action}</dd>
          <dt>Outcome</dt>
          <dd>{statusHint(entry)}</dd>
        </dl>
      </div>

      {entry.targetType || entry.targetLabel ? (
        <div className="ld-sec">
          <h4>Subject</h4>
          <dl className="ld-kv">
            {entry.targetType ? (
              <>
                <dt>Kind</dt>
                <dd>{entry.targetType}</dd>
              </>
            ) : null}
            {entry.targetLabel ? (
              <>
                <dt>Name</dt>
                <dd>{entry.targetLabel}</dd>
              </>
            ) : null}
            {/* A null id where a label survives is an account that has since been erased —
                the label is kept on purpose, the reference is not. */}
            {entry.targetType === 'user' && !entry.targetId ? (
              <>
                <dt>Account</dt>
                <dd>deleted since</dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}

      {pairs.length ? (
        <div className="ld-sec">
          <h4>What it recorded</h4>
          <dl className="ld-kv">
            {pairs.map(([k, v]) => (
              <Fragment key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ) : null}

      {revisionId ? (
        <div className="ld-sec">
          <h4>Where it leads</h4>
          <button className="btn" onClick={() => callAction('openLogDetail', revisionId)}>
            <span>Open the document version</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
